import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../components/layout/PublicHeader';
import PublicFooter from '../components/layout/PublicFooter';
import API_CONFIG from '../config/api';

interface FAQ {
  _id: string;
  question: string;
  answer: string;
  category: 'general' | 'test-request' | 'test-results' | 'payment' | 'technical';
  active: boolean;
  order: number;
  isOpen: boolean;
}

interface GuideItemProps {
  title: string;
  content: string;
  isOpen?: boolean;
}

const GuideItem: React.FC<GuideItemProps> = ({ title, content, isOpen = false }) => {
  const [expanded, setExpanded] = useState(isOpen);

  return (
    <div className="border border-slate-200 rounded-lg mb-4 overflow-hidden">
      <div 
        className={`p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 ${expanded ? 'bg-blue-50' : 'bg-white'}`}
        onClick={() => setExpanded(!expanded)}
      >
        <h3 className={`font-bold ${expanded ? 'text-blue-700' : 'text-slate-800'}`}>{title}</h3>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className={`h-5 w-5 transition-transform ${expanded ? 'transform rotate-180 text-blue-600' : 'text-slate-500'}`} 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {expanded && (
        <div className="p-4 border-t border-slate-100 bg-white">
          <p className="text-slate-700 leading-relaxed">{content}</p>
        </div>
      )}
    </div>
  );
};

const Guide: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Fetch FAQs from API
  const fetchFaqs = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_CONFIG.getUrl('faqs/public'));
      
      if (!response.ok) {
        throw new Error('خطا در دریافت سوالات متداول');
      }
      
      const data = await response.json();
      setFaqs(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در دریافت سوالات متداول');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  // Function to go back to the previous page
  const goBack = () => {
    window.history.back();
  };

  // Get category label
  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'general': return 'عمومی';
      case 'test-request': return 'درخواست آزمایش';
      case 'test-results': return 'نتایج آزمایش';
      case 'payment': return 'پرداخت';
      case 'technical': return 'فنی';
      default: return category;
    }
  };

  // Filter FAQs by category
  const filteredFaqs = selectedCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === selectedCategory);

  // Group FAQs by category
  const groupedFaqs = faqs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {} as Record<string, FAQ[]>);

  return (
    <div className="flex flex-col min-h-screen bg-slate-50">
      {/* Header */}
      <PublicHeader />

      {/* Main Content */}
      <main className="flex-grow container mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-8 text-center">راهنمای استفاده از خدمات آزمایشگاه رازی</h1>
          
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="mb-8">
              <p className="text-slate-700 leading-relaxed">
                به راهنمای آزمایشگاه رازی خوش آمدید. در این بخش، اطلاعاتی در مورد نحوه استفاده از خدمات آزمایشگاه، روند انجام آزمایش و سایر اطلاعات مورد نیاز ارائه شده است. برای مشاهده هر بخش، روی عنوان آن کلیک کنید.
              </p>
            </div>

            {/* Category Filter */}
            <div className="mb-8">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  همه
                </button>
                {Object.keys(groupedFaqs).map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedCategory === category
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {getCategoryLabel(category)}
                  </button>
                ))}
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex justify-center items-center py-8">
                <div className="text-lg">در حال بارگذاری...</div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-8">
                {error}
              </div>
            )}

            {/* Dynamic FAQ Content */}
            {!loading && !error && (
              <>
                {selectedCategory === 'all' ? (
                  // Show all FAQs grouped by category
                  Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
                    <div key={category} className="mb-8">
                      <h2 className="text-xl font-bold text-slate-800 mb-4">
                        {getCategoryLabel(category)}
                      </h2>
                      {categoryFaqs.map(faq => (
                        <GuideItem
                          key={faq._id}
                          title={faq.question}
                          content={faq.answer}
                          isOpen={faq.isOpen}
                        />
                      ))}
                    </div>
                  ))
                ) : (
                  // Show filtered FAQs
                  <div className="mb-8">
                    <h2 className="text-xl font-bold text-slate-800 mb-4">
                      {getCategoryLabel(selectedCategory)}
                    </h2>
                    {filteredFaqs.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        سوال متداولی برای این دسته‌بندی موجود نیست.
                      </div>
                    ) : (
                      filteredFaqs.map(faq => (
                        <GuideItem
                          key={faq._id}
                          title={faq.question}
                          content={faq.answer}
                          isOpen={faq.isOpen}
                        />
                      ))
                    )}
                  </div>
                )}
              </>
            )}
            
            <div className="mt-12 text-center">
              <button 
                onClick={goBack}
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                بازگشت
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

export default Guide; 